import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import HistorialAccionesDatePicker from "./HistorialAccionesDatePicker";
import HistorialAccionesSelect from "./HistorialAccionesSelect";
import { useLanguage } from "context/LanguageContext";

const PRIMARY_BORDER = "rgba(30, 134, 246, 0.28)";

const fieldShellSx = {
  flex: "1 1 200px",
  minWidth: { xs: "100%", sm: "200px" },
};

function FieldLabel({ children, htmlFor }) {
  return (
    <ArgonTypography
      component="label"
      htmlFor={htmlFor}
      variant="caption"
      sx={{
        display: "block",
        mb: 0.75,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#67748e",
      }}
    >
      {children}
    </ArgonTypography>
  );
}

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  htmlFor: PropTypes.string.isRequired,
};

function HistorialAccionesFiltersPanel({
  filtroUserId,
  onFiltroUserIdChange,
  filtroAccion,
  onFiltroAccionChange,
  fechaDesde,
  onFechaDesdeChange,
  fechaHasta,
  onFechaHastaChange,
  funcionariosOpciones,
  tiposAccionUnicos,
}) {
  const { t } = useLanguage();
  const opcionesFuncionarios = useMemo(
    () =>
      funcionariosOpciones.map(({ id, label }) => ({
        value: String(id),
        label,
      })),
    [funcionariosOpciones]
  );

  const opcionesAcciones = useMemo(
    () => tiposAccionUnicos.map((tipo) => ({ value: tipo, label: tipo })),
    [tiposAccionUnicos]
  );

  return (
    <ArgonBox
      sx={{
        mx: { xs: 2, sm: 3 },
        mb: 2,
        p: { xs: 2, sm: 2.5 },
        borderRadius: "16px",
        background: "linear-gradient(165deg, #f8fafc 0%, #ffffff 55%, #fbfcfe 100%)",
        border: `1px solid ${PRIMARY_BORDER}`,
        boxShadow: "0 4px 24px -6px rgba(52, 71, 103, 0.12), inset 0 1px 0 rgba(255,255,255,0.95)",
      }}
    >
      <ArgonBox
        display="flex"
        flexWrap="wrap"
        alignItems="flex-end"
        sx={{ gap: { xs: 2, sm: 2.25 } }}
      >
        <ArgonBox sx={fieldShellSx}>
          <FieldLabel htmlFor="historial-filtro-funcionario">{t('history.filter.officer')}</FieldLabel>
          <HistorialAccionesSelect
            id="historial-filtro-funcionario"
            value={filtroUserId === undefined || filtroUserId === null ? "" : String(filtroUserId)}
            onChange={onFiltroUserIdChange}
            options={opcionesFuncionarios}
            emptyLabel={t('history.filter.allOfficers')}
            placeholder={t('history.filter.allOfficers')}
          />
        </ArgonBox>

        <ArgonBox sx={fieldShellSx}>
          <FieldLabel htmlFor="historial-filtro-accion">{t('history.filter.actionType')}</FieldLabel>
          <HistorialAccionesSelect
            id="historial-filtro-accion"
            value={filtroAccion || ""}
            onChange={onFiltroAccionChange}
            options={opcionesAcciones}
            emptyLabel={t('history.filter.allActions')}
            placeholder={t('history.filter.allActions')}
          />
        </ArgonBox>

        <ArgonBox
          sx={{
            ...fieldShellSx,
            flex: "1 1 160px",
            minWidth: "160px",
            maxWidth: { sm: "220px" },
          }}
        >
          <FieldLabel htmlFor="historial-filtro-desde">{t('history.filter.from')}</FieldLabel>
          <HistorialAccionesDatePicker
            id="historial-filtro-desde"
            value={fechaDesde || ""}
            onChange={onFechaDesdeChange}
            maxDate={fechaHasta || undefined}
            placeholder={t('history.filter.chooseDate')}
          />
        </ArgonBox>

        <ArgonBox
          sx={{
            ...fieldShellSx,
            flex: "1 1 160px",
            minWidth: "160px",
            maxWidth: { sm: "220px" },
          }}
        >
          <FieldLabel htmlFor="historial-filtro-hasta">{t('history.filter.to')}</FieldLabel>
          <HistorialAccionesDatePicker
            id="historial-filtro-hasta"
            value={fechaHasta || ""}
            onChange={onFechaHastaChange}
            minDate={fechaDesde || undefined}
            placeholder={t('history.filter.chooseDate')}
          />
        </ArgonBox>
      </ArgonBox>
    </ArgonBox>
  );
}

HistorialAccionesFiltersPanel.propTypes = {
  filtroUserId: PropTypes.string.isRequired,
  onFiltroUserIdChange: PropTypes.func.isRequired,
  filtroAccion: PropTypes.string.isRequired,
  onFiltroAccionChange: PropTypes.func.isRequired,
  fechaDesde: PropTypes.string.isRequired,
  onFechaDesdeChange: PropTypes.func.isRequired,
  fechaHasta: PropTypes.string.isRequired,
  onFechaHastaChange: PropTypes.func.isRequired,
  funcionariosOpciones: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  tiposAccionUnicos: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default HistorialAccionesFiltersPanel;
